import { zodResolver } from '@hookform/resolvers/zod';
import { personNameSchema } from '@baby-tracker/validation';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';

import { routePaths } from '../app/route-paths.js';
import styles from '../components/layout/AuthLayout.module.css';
import { Button } from '../components/ui/Button.js';
import { FormError } from '../components/ui/FormError.js';
import fieldStyles from '../components/ui/fields.module.css';
import { TextField } from '../components/ui/TextField.js';
import { authClient } from '../lib/auth-client.js';
import { authErrorMessage } from '../lib/auth-errors.js';
import { returnToQuery, safeReturnTo } from '../lib/return-to.js';
import { waitForSignedInSession } from '../lib/session.js';

const signUpSchema = z.object({
    name: personNameSchema,
    email: z.email('Enter a valid email address'),
    password: z.string().min(10, 'Use at least 10 characters'),
});

type SignUpValues = z.infer<typeof signUpSchema>;

export function SignUpPage(): ReactElement {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [serverError, setServerError] = useState<string | null>(null);
    const form = useForm<SignUpValues>({
        resolver: zodResolver(signUpSchema),
        defaultValues: { name: '', email: '', password: '' },
    });

    async function onSubmit(values: SignUpValues): Promise<void> {
        setServerError(null);
        const returnTo = params.get('returnTo');
        const destination = returnTo ? safeReturnTo(returnTo) : routePaths.today;
        const result = await authClient.signUp.email({
            name: values.name,
            email: values.email,
            password: values.password,
            // Where the verification link lands. Better Auth appends an error query on failure.
            callbackURL: `${routePaths.verifyEmail}?next=${encodeURIComponent(destination)}`,
        });
        if (result.error) {
            setServerError(authErrorMessage(result.error, 'Could not create an account'));
            return;
        }
        if (result.data.token === null) {
            // Verification required: no session yet.
            await navigate(`${routePaths.checkEmail}?email=${encodeURIComponent(values.email)}`);
            return;
        }
        const signedIn = await waitForSignedInSession();
        if (!signedIn) {
            setServerError('Could not create an account');
            return;
        }
        await navigate(destination);
    }

    return (
        <>
            <h1>Create an account</h1>
            <form
                className={fieldStyles.stack}
                noValidate
                onSubmit={(event) => {
                    void form.handleSubmit(onSubmit)(event);
                }}
            >
                <TextField
                    label="Name"
                    autoComplete="name"
                    error={form.formState.errors.name?.message}
                    {...form.register('name')}
                />
                <TextField
                    label="Email"
                    type="email"
                    autoComplete="email"
                    error={form.formState.errors.email?.message}
                    {...form.register('email')}
                />
                <TextField
                    label="Password"
                    type="password"
                    autoComplete="new-password"
                    error={form.formState.errors.password?.message}
                    {...form.register('password')}
                />
                <FormError message={serverError} />
                <Button type="submit" loading={form.formState.isSubmitting}>
                    Create account
                </Button>
            </form>
            <p className={styles.links}>
                Already have an account?{' '}
                <Link to={`${routePaths.signIn}${returnToQuery(params.get('returnTo'))}`}>
                    Sign in
                </Link>
            </p>
        </>
    );
}
